import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Car, Users, Receipt, Settings as SettingsIcon, LogOut, Menu, X, Users2, BarChart3 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/veiculos', label: 'Veículos', icon: Car },
  { to: '/socios', label: 'Sócios', icon: Users },
  { to: '/pessoas', label: 'Pessoas', icon: Users2 },
  { to: '/despesas', label: 'Despesas', icon: Receipt },
  { to: '/relatorio-veiculos', label: 'Relatório Veículos', icon: BarChart3 },
  { to: '/configuracoes', label: 'Configurações', icon: SettingsIcon },
];

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2 px-2 pb-4" data-testid="sidebar-brand">
      <div className="h-8 w-8 rounded-lg bg-[hsl(var(--primary))] grid place-items-center text-white font-heading font-bold">A</div>
      <div className="font-heading font-semibold text-base tracking-tight">AutoSócios</div>
    </Link>
  );
}

function NavItems({ onClick }) {
  return (
    <nav className="flex-1 space-y-1">
      {NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onClick}
            data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${
                isActive
                  ? 'bg-muted text-foreground font-medium before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:rounded-r before:bg-[hsl(var(--primary))]'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`
            }
          >
            <Icon className="h-4 w-4" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function AppShell({ children }) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = (user?.name || '?')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-[var(--app-bg)]">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col fixed inset-y-0 left-0 bg-card border-r border-border/70 px-3 py-4" data-testid="app-sidebar">
        <Brand />
        <NavItems />
        <div className="border-t border-border/70 pt-3 mt-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-[hsl(var(--primary))] text-white text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{tenant?.name}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                logout();
                navigate('/login');
              }}
              title="Sair"
              data-testid="sidebar-logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Topbar mobile */}
      <div className="lg:hidden fixed inset-x-0 top-0 z-40 h-14 border-b border-border/70 bg-card flex items-center justify-between px-4">
        <Brand />
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-4">
            <div className="flex items-center justify-between mb-4">
              <Brand />
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <NavItems onClick={() => setMobileOpen(false)} />
            <div className="border-t border-border/70 pt-3 mt-3">
              <Button
                variant="ghost"
                className="w-full justify-start gap-2"
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
              >
                <LogOut className="h-4 w-4" /> Sair
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <main className="flex-1 lg:pl-64 pt-14 lg:pt-0">
        <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-[1280px] 2xl:max-w-[1440px] mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
